import React, { useEffect } from 'react';
import HelpLayout from '../../../components/help/HelpLayout';

const AnnouncementsHelp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'Admin Guide', href: '/help/admin/dashboard' },
    { name: 'Announcements' }
  ];

  return (
    <HelpLayout title="Announcements System" breadcrumbs={breadcrumbs}>
      <div className="help-content">
        <div className="card">
          <div className="card-body">
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              Announcements System Guide
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6
            }}>
              Create and manage system-wide announcements, target specific audiences, and schedule notifications.
            </p>

            {/* Announcements Overview */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                📢 Announcements System Overview
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Accessing Announcements Management
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  Announcement management is handled through the Django Admin interface:
                </p>
                <ol style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li>Navigate to <strong>/admin/</strong> from the main application</li>
                  <li>Log in with your administrator credentials</li>
                  <li>Access <strong>Notifications → Announcements</strong> for announcement management</li>
                  <li>Use <strong>Notifications → User notifications</strong> for individual notifications</li>
                </ol>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Announcement Categories
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '6px',
                    border: '1px solid #ef4444'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#dc2626',
                      marginBottom: '8px'
                    }}>
                      🚨 Course Deadlines
                    </h5>
                    <p style={{ fontSize: '14px', color: '#dc2626', margin: 0 }}>
                      Application deadlines, registration cutoffs, and important course dates
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#e0f2fe',
                    borderRadius: '6px',
                    border: '1px solid #0284c7'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#0284c7',
                      marginBottom: '8px'
                    }}>
                      🔧 System Updates
                    </h5>
                    <p style={{ fontSize: '14px', color: '#0284c7', margin: 0 }}>
                      Platform maintenance, new features, and system changes
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '6px',
                    border: '1px solid #16a34a'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#16a34a',
                      marginBottom: '8px'
                    }}>
                      🎓 Training Opportunities
                    </h5>
                    <p style={{ fontSize: '14px', color: '#16a34a', margin: 0 }}>
                      New course launches, special programs, and training initiatives
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    border: '1px solid #f59e0b'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#d97706',
                      marginBottom: '8px'
                    }}>
                      🏛️ Government Initiatives
                    </h5>
                    <p style={{ fontSize: '14px', color: '#d97706', margin: 0 }}>
                      Policy updates, funding announcements, and government programs
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '6px',
                    border: '1px solid #6b7280'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6b7280',
                      marginBottom: '8px'
                    }}>
                      📰 General News
                    </h5>
                    <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                      General updates, success stories, and community news
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Creating Announcements */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                ✍️ Creating Announcements
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Announcement Creation Process
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '8px',
                    borderLeft: '4px solid var(--primary-yellow)'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        backgroundColor: 'var(--primary-yellow)',
                        color: 'var(--primary-black)',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}>1</span>
                      Basic Information
                    </h5>
                    <ul style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, paddingLeft: '20px' }}>
                      <li>Title: Clear, descriptive announcement title</li>
                      <li>Content: Detailed announcement message</li>
                      <li>Category: Select appropriate category</li>
                      <li>Priority: Set urgency level (low, medium, high, critical)</li>
                    </ul>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '8px',
                    borderLeft: '4px solid var(--primary-yellow)'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        backgroundColor: 'var(--primary-yellow)',
                        color: 'var(--primary-black)',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}>2</span>
                      Targeting and Scheduling
                    </h5>
                    <ul style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, paddingLeft: '20px' }}>
                      <li>Target audience: All users or specific groups</li>
                      <li>Publication date: When announcement becomes visible</li>
                      <li>Expiry date: When announcement is automatically hidden</li>
                      <li>Active status: Enable/disable announcement visibility</li>
                    </ul>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '8px',
                    borderLeft: '4px solid var(--primary-yellow)'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        backgroundColor: 'var(--primary-yellow)',
                        color: 'var(--primary-black)',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}>3</span>
                      Notification Settings
                    </h5>
                    <ul style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, paddingLeft: '20px' }}>
                      <li>Email notifications: Send to user email addresses</li>
                      <li>Push notifications: In-app notification alerts</li>
                      <li>Dashboard display: Show in user dashboard</li>
                      <li>Notification bell: Update unread count</li>
                    </ul>
                  </div>
                </div>
              </div>
            {/* Priority Levels */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                🚨 Priority Levels and Targeting
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Announcement Priority Levels
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '6px',
                    borderLeft: '4px solid #ef4444'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#dc2626',
                      marginBottom: '4px'
                    }}>
                      🔴 Critical Priority
                    </h5>
                    <p style={{ fontSize: '14px', color: '#dc2626', margin: 0 }}>
                      System outages, urgent deadlines, emergency notifications. Displayed prominently with animations.
                    </p>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    borderLeft: '4px solid #f59e0b'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#d97706',
                      marginBottom: '4px'
                    }}>
                      🟡 High Priority
                    </h5>
                    <p style={{ fontSize: '14px', color: '#d97706', margin: 0 }}>
                      Important deadlines, course changes, significant updates. Featured prominently in dashboard.
                    </p>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#e0f2fe',
                    borderRadius: '6px',
                    borderLeft: '4px solid #0284c7'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#0284c7',
                      marginBottom: '4px'
                    }}>
                      🔵 Medium Priority
                    </h5>
                    <p style={{ fontSize: '14px', color: '#0284c7', margin: 0 }}>
                      General updates, new features, course announcements. Standard dashboard display.
                    </p>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '6px',
                    borderLeft: '4px solid #6b7280'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                    ⚪ Low Priority
                    </h5>
                    <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                      General information, tips, success stories. Subtle display in announcements section.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Audience Targeting
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  Target announcements to specific user groups for relevant messaging:
                </p>
                <ul style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li><strong>All Users:</strong> System-wide announcements visible to everyone</li>
                  <li><strong>Active Learners:</strong> Users with current course registrations</li>
                  <li><strong>Applicants:</strong> Users with pending applications</li>
                  <li><strong>Staff Members:</strong> Administrative users and GCRA staff</li>
                  <li><strong>Course-Specific:</strong> Students in particular courses or programs</li>
                  <li><strong>Geographic:</strong> Users in specific economic corridors or regions</li>
                </ul>
              </div>
            </div>

            {/* Notification Management */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                🔔 Notification Management
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Automatic Notification Generation
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  The system automatically creates individual notifications for users based on announcements:
                </p>
                <ul style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li><strong>User Notifications:</strong> Individual notification records for each user</li>
                  <li><strong>Read Status Tracking:</strong> Monitor which users have seen announcements</li>
                  <li><strong>Notification Bell:</strong> Update unread count in navigation</li>
                  <li><strong>Dashboard Integration:</strong> Display in user dashboard announcements section</li>
                </ul>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Monitoring Announcement Effectiveness
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '6px',
                    textAlign: 'center'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#16a34a',
                      marginBottom: '8px'
                    }}>
                      📊 Read Statistics
                    </h5>
                    <p style={{ fontSize: '14px', color: '#16a34a', margin: 0 }}>
                      Track how many users have read each announcement
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    textAlign: 'center'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#d97706',
                      marginBottom: '8px'
                    }}>
                      ⏱️ Response Time
                    </h5>
                    <p style={{ fontSize: '14px', color: '#d97706', margin: 0 }}>
                      Monitor how quickly users engage with announcements
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#e0f2fe',
                    borderRadius: '6px',
                    textAlign: 'center'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#0284c7',
                      marginBottom: '8px'
                    }}>
                      🎯 Reach Analysis
                    </h5>
                    <p style={{ fontSize: '14px', color: '#0284c7', margin: 0 }}>
                      Analyze which user groups are most engaged
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Best Practices */}
            <div style={{
              padding: '20px',
              backgroundColor: 'var(--background-gray)',
              borderRadius: '8px',
              marginTop: '32px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                💡 Announcements Best Practices
              </h3>
              <ul style={{
                color: 'var(--text-gray)',
                lineHeight: 1.6,
                paddingLeft: '20px',
                margin: 0
              }}>
                <li><strong>Clear and concise:</strong> Write announcements in simple, understandable language</li>
                <li><strong>Appropriate priority:</strong> Use critical priority sparingly for true emergencies</li>
                <li><strong>Timely publication:</strong> Schedule announcements to reach users when most active</li>
                <li><strong>Relevant targeting:</strong> Send announcements only to users who need the information</li>
                <li><strong>Regular cleanup:</strong> Remove expired announcements to keep content fresh</li>
                <li><strong>Consistent formatting:</strong> Use standard templates for similar announcement types</li>
                <li><strong>Action-oriented:</strong> Include clear next steps when user action is required</li>
                <li><strong>Monitor engagement:</strong> Track read rates and adjust communication strategies</li>
                <li><strong>Avoid spam:</strong> Don't overwhelm users with too many announcements</li>
                <li><strong>Test before publishing:</strong> Review announcements for accuracy and clarity</li>
              </ul>
            </div>

            </div>
          </div>
        </div>
      </div>
    </HelpLayout>
  );
};

export default AnnouncementsHelp;
